// web/src/components/records/RecordsTable.tsx — the records dashboard table
// (wireframe 02). One row per record the caller owns or holds a shard in: name,
// K-of-N, status, derived quorum-health badge, window, and the caller's role.
// Row click opens the detail; a per-row "Convene unseal" runs the break-glass flow.
// Table on wide screens, cards below md (responsive in shell.css). METADATA ONLY.
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, Zap } from 'lucide-react';
import type { RecordSummary } from '../../../../shared/types';
import { Icon } from '../ui/Icon';
import { Badge } from '../ui';
import { quorumHealth, recordName, roleLabel, statusBadge, windowLabel } from '../../lib/records';

export function RecordsTable({
  records,
  liveCeremonyIds,
  onConvene,
}: {
  records: RecordSummary[];
  /** Record ids with an open ceremony — drives the "Breaking glass" badge. */
  liveCeremonyIds?: ReadonlySet<string>;
  onConvene: (r: RecordSummary) => void;
}): React.JSX.Element {
  const navigate = useNavigate();
  const open = (id: string): void => navigate(`/records/${id}`);

  return (
    <div className="bgTableWrap" role="region" aria-label="Records">
      <table className="bgTable">
        <thead>
          <tr>
            <th scope="col">Name</th>
            <th scope="col">Quorum</th>
            <th scope="col">Status</th>
            <th scope="col">Health</th>
            <th scope="col">Window</th>
            <th scope="col">Role</th>
            <th scope="col" className="bgTableActions">
              <span className="sr-only">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {records.map((r) => {
            const live = liveCeremonyIds?.has(r.id) ?? false;
            const health = quorumHealth(r, live);
            const status = statusBadge(r.status);
            return (
              <tr
                key={r.id}
                className="bgTableRow"
                tabIndex={0}
                role="link"
                aria-label={`Open ${recordName(r)}`}
                onClick={() => open(r.id)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    open(r.id);
                  }
                }}
              >
                <td data-label="Name">
                  <span className="bgRecCell">
                    <span className="bgLock" aria-hidden="true">
                      <Icon icon={Lock} size="sm" />
                    </span>
                    <span className="bgRecName">{recordName(r)}</span>
                  </span>
                </td>
                <td data-label="Quorum" className="bgMono">
                  {r.thresholdK} of {r.totalN}
                </td>
                <td data-label="Status">
                  <Badge tone={status.tone} label={status.label} />
                </td>
                <td data-label="Health">
                  <span title={health.tooltip}>
                    <Badge tone={health.tone} label={health.label} />
                  </span>
                </td>
                <td data-label="Window" className="bgMono">
                  {windowLabel(r.timeoutSeconds)}
                </td>
                <td data-label="Role" className="bgMono bgRecRole">
                  {roleLabel(r)}
                </td>
                <td className="bgTableActions">
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      onConvene(r);
                    }}
                    aria-label={`Convene unseal for ${recordName(r)}`}
                    disabled={r.status !== 'sealed'}
                  >
                    <Icon icon={Zap} size="xs" /> Convene unseal
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default RecordsTable;
